// @flow

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Source, Layer } from 'react-mapbox-gl';
import SuperCluster from 'supercluster';
import { connect } from 'react-redux';
import type { GeoJSONSource } from 'mapbox-gl';

import {
  selectListingMarkersSet,
  selectFlatTileJSONBounds,
  selectRoundedZoomInteger,
} from '../modules/map';

import type { Listing } from '../types';
import labelBestSrc from './icons/label-best.svg';
import labelBetterSrc from './icons/label-better.svg';
import labelGoodSrc from './icons/label-good.svg';
import labelClusteredSrc from './icons/label-clustered.svg';

type Props = {
  listings: Array<Listing>,
  bounds: null | [number, number, number, number],
  zoom: number,
};

type State = {
  sources: Array<GeoJSONSource>,
};

const MAX_ZOOM = 8;

const textPaintStyle = {
  'text-size': 16,
  'text-font': ['Roboto Regular'],
};

const imagePaintStyle = {
  ...textPaintStyle,
  // eslint-disable-next-line no-template-curly-in-string
  'text-field': '${price}',
  'icon-text-fit': 'width',
  'icon-text-fit-padding': [8, 15, 4, 15],
  'icon-allow-overlap': true,
  'text-allow-overlap': true,
};

const layout = {
  clustered: {
    ...imagePaintStyle,
    'icon-image': 'label-clustered',
  },
  good: {
    ...imagePaintStyle,
    'icon-image': 'label-good',
  },
  better: {
    ...imagePaintStyle,
    'icon-image': 'label-better',
  },
  best: {
    ...imagePaintStyle,
    'icon-image': 'label-best',
  },
  clusterText: {
    ...textPaintStyle,
    'text-field': '{point_count}',
  },
};

const paint = {
  clustered: {
    'text-color': '#000',
  },
  good: {
    'text-color': '#fff',
  },
  better: {
    'text-color': '#fff',
  },
  best: {
    'text-color': '#fff',
  },
  clusterCircle: {
    'circle-color': '#000',
    'circle-radius': 20,
  },
  clusterText: {
    'text-color': '#fff',
  },
};

const filter = {
  clustered: ['==', ['get', 'quality'], 'clustered'],
  good: ['==', ['get', 'quality'], 'good'],
  better: ['==', ['get', 'quality'], 'better'],
  best: ['==', ['get', 'quality'], 'best'],
  cluster: ['has', 'point_count'],
};

const { Image } = window;

const getImage = (src) => {
  const image = new Image(96, 96);
  image.src = src;

  return image;
};

const images = {
  clustered: ['label-clustered', getImage(labelClusteredSrc), { pixelRatio: 4 }],
  good: ['label-good', getImage(labelGoodSrc), { pixelRatio: 4 }],
  better: ['label-better', getImage(labelBetterSrc), { pixelRatio: 4 }],
  best: ['label-best', getImage(labelBestSrc), { pixelRatio: 4 }],
};

const qualities = ['best', 'better', 'good', 'clustered', 'bad', 'bad-split'];
const qualityRanks = qualities.reduce((ranks, quality, i) => ({
  ...ranks,
  [quality]: i,
}), {});

const listingToFeature = ({ coordinates, ...properties }) => ({
  type: 'Feature',
  geometry: { type: 'Point', coordinates },
  properties,
});

class ListingMarkerStyle extends PureComponent<Props, State> {
  static contextTypes = {
    map: PropTypes.object.isRequired,
  };

  state = {
    sources: this.getSources(),
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.props.listings !== nextProps.listings ||
      this.props.bounds !== nextProps.bounds
    ) {
      this.setState({ sources: this.getSources(nextProps) });
    }
  }

  getSources({ bounds, listings } = this.props) {
    const sources = [];

    if (!bounds || !listings) return sources;

    const supercluster = SuperCluster({
      radius: 50,
      maxZoom: MAX_ZOOM - 1,
      initial: () => ({
        price: Infinity,
        quality: 'clustered',
      }),
      map: ({ price = Infinity, quality = 'clustered' }) => ({ price, quality }),
      reduce(cluster, { price, quality }) {
        // eslint-disable-next-line no-param-reassign
        cluster.price = Math.min(cluster.price, price);

        if (qualityRanks[quality] < qualityRanks[cluster.quality]) {
          // eslint-disable-next-line no-param-reassign
          cluster.quality = quality;
        }
      },
    });

    supercluster.load(listings.map(listingToFeature));

    for (let zoom = 0; zoom <= MAX_ZOOM; zoom += 1) {
      const features = supercluster.getClusters(bounds, zoom)
        .map(feature => ({
          ...feature,
          properties: {
            ...feature.properties,
            zoom,
          },
        }));

      sources.push({
        type: 'geojson',
        data: { type: 'FeatureCollection', features },
      });
    }

    return sources;
  }

  render() {
    return (
      <div>
        <Source id="listing-markers" geoJsonSource={this.state.sources[this.props.zoom]} />
        <Layer
          id="clustered"
          sourceId="listing-markers"
          type="symbol"
          paint={paint.clustered}
          layout={layout.clustered}
          images={images.clustered}
          filter={filter.clustered}
        />
        <Layer
          id="good"
          sourceId="listing-markers"
          type="symbol"
          paint={paint.good}
          layout={layout.good}
          images={images.good}
          filter={filter.good}
        />
        <Layer
          id="better"
          sourceId="listing-markers"
          type="symbol"
          paint={paint.better}
          layout={layout.better}
          images={images.better}
          filter={filter.better}
        />
        <Layer
          id="best"
          sourceId="listing-markers"
          type="symbol"
          paint={paint.best}
          layout={layout.best}
          images={images.best}
          filter={filter.best}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  listings: selectListingMarkersSet(state),
  bounds: selectFlatTileJSONBounds(state),
  zoom: selectRoundedZoomInteger(state),
});

export default connect(mapStateToProps, null)(ListingMarkerStyle);



// WEBPACK FOOTER //
// ./src/styles/listing-markers.js